import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

const CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes

async function refreshWithFreshServiceWorker() {
  // If a newer SW is sitting in `waiting`, tell it to skip so the reload
  // is served by the new version. Otherwise a soft reload may still get
  // intercepted by the old SW's caches.
  try {
    if ('serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg && reg.waiting) {
        reg.waiting.postMessage({ type: 'SKIP_WAITING' });
      }
    }
  } catch {
    // Ignore — fall through to a plain reload.
  }
  window.location.reload();
}

export function useVersionCheck() {
  const currentVersion = useRef(null);
  const toastShown = useRef(false);

  useEffect(() => {
    // Read the build version from the meta tag injected by SSR
    const meta = document.querySelector('meta[name="build-version"]');
    if (meta) {
      currentVersion.current = meta.getAttribute('content');
    }

    const checkVersion = async () => {
      try {
        const res = await fetch('/app-version', { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();

        if (currentVersion.current && data.version && data.version !== currentVersion.current && !toastShown.current) {
          toastShown.current = true;
          toast('A new version of Zuriya is available', {
            description: 'Tap to refresh and get the latest experience.',
            duration: Infinity,
            action: {
              label: 'Refresh',
              onClick: refreshWithFreshServiceWorker,
            },
          });
        }
      } catch (err) {
        console.error('Version check error:', err);
      }
    };

    // First check after 30 seconds (give page time to load)
    const initialTimeout = setTimeout(checkVersion, 30 * 1000);

    // Then check periodically
    const interval = setInterval(checkVersion, CHECK_INTERVAL);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, []);
}
